import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle, Zap, Shield, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AnimatedGroup } from '@/components/ui/animated-group';
import { useUnlock } from '@/hooks/useUnlock';
import { toast } from 'sonner';

export default function Unlock() {
  const navigate = useNavigate();
  const { isUnlocked, unlock } = useUnlock();

  // Redirect if already unlocked
  React.useEffect(() => {
    if (isUnlocked) {
      navigate('/tools');
      return;
    }
  }, [isUnlocked, navigate]);

  const benefits = [
    {
      icon: <Zap className="h-5 w-5" />,
      title: "300+ Curated Tools",
      description: "Hand-picked design tools for every workflow"
    },
    {
      icon: <CheckCircle className="h-5 w-5" />,
      title: "Goal-Based Paths", 
      description: "Find tools organized by your specific needs"
    },
    {
      icon: <Shield className="h-5 w-5" />,
      title: "Always Updated",
      description: "Fresh tools and resources added regularly"
    },
    {
      icon: <Star className="h-5 w-5" />,
      title: "Lifetime Access",
      description: "One payment, unlimited access forever"
    }
  ];

  const handleSupported = () => {
    try { 
      localStorage.setItem("designstack_supported", "true"); 
    } catch {}
    toast.success("Thanks for your support! 🎉 Access unlocked.");
    setTimeout(() => {
      navigate('/auth');
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with close button */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg">
              D
            </div>
            <span className="text-xl font-bold font-poppins">DesignStack</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/')}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pb-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 blur-3xl -z-10"></div>
              <h1 className="text-4xl md:text-6xl font-poppins font-bold bg-gradient-to-r from-text-primary via-primary to-text-primary bg-clip-text text-transparent mb-6 leading-tight">
                Unlock Lifetime Access
              </h1>
            </div>
            <div className="space-y-2">
              <div className="text-3xl md:text-5xl font-poppins font-black bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                Pay What You Want
              </div>
              <div className="text-xl md:text-2xl text-text-secondary font-inter font-light tracking-wide">
                support the project
              </div>
            </div>
            <div className="mt-6 w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full"></div>
          </div>

          {/* Benefits Grid */}
          <AnimatedGroup preset="fade" className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 rounded-lg bg-surface border">
                <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-accent-light flex items-center justify-center text-accent">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary font-poppins text-sm">{benefit.title}</h3>
                  <p className="text-text-secondary text-sm font-inter">{benefit.description}</p>
                </div>
              </div>
            ))}
          </AnimatedGroup>

          {/* BMC Button Section */}
          <div className="flex flex-col items-center space-y-6 p-6 bg-surface rounded-xl border mb-8">
            <div className="text-center">
              <h3 className="font-semibold text-text-primary font-poppins mb-2">Support & Get Access</h3>
              <p className="text-sm text-text-secondary font-inter mb-4">
                Pay any amount to support the project and unlock access
              </p>
            </div>
            
            {/* Buy Me a Coffee Button */}
            <div className="flex justify-center">
              <a href="https://www.buymeacoffee.com/paulofhreik" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://cdn.buymeacoffee.com/buttons/v2/arial-yellow.png" 
                  alt="Buy Me A Coffee" 
                  style={{height: '60px', width: '217px'}} 
                />
              </a>
            </div>

          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-3 mb-6">
            <Button
              onClick={handleSupported}
              size="lg"
              className="w-full bg-gradient-to-r from-primary to-primary-glow hover:from-primary-glow hover:to-primary text-primary-foreground font-semibold py-6 px-8 rounded-xl shadow-elegant hover:shadow-glow transition-all duration-300 font-poppins"
            >
              I supported 🙂
            </Button>
          </div>

          {/* Footer Note */}
          <p className="text-center text-sm text-text-secondary font-inter">
            Payments securely processed via <strong>Buy Me a Coffee</strong>
          </p>
        </div>
      </div>

    </div>
  );
}